/* oxlint-disable max-lines, max-dependencies -- query orchestration + retry logic is cohesive; splitting would scatter tightly-coupled control flow */
import { createHash } from "node:crypto";
import { existsSync, readFileSync, truncateSync } from "node:fs";
import { sessionTranscriptPath } from "../harness/pty/settings.js";
import type {
  HarnessEvent,
  HarnessQueryOptions,
  HarnessHookInput,
  HarnessHookOutput,
  HarnessKind,
} from "../harness/index.js";
import { createServiceLogger } from "../utils/logger.js";
import type { TaskContext, MultimodalBlock, AgentMode } from "@project/shared";
import type { AgentConnection } from "../connection/agent-connection.js";
import type { AgentRunnerConfig } from "../runner-types.js";
import { buildInitialPrompt } from "./prompt-builder.js";
import { buildSystemPrompt } from "./system-prompt.js";
import { createConveyorMcpServer } from "../tools/index.js";
import { resolvePlaywrightMcpServer } from "./playwright-mcp.js";
import { processEvents } from "./event-processor.js";
import { applyCycledKeyEnv, syncCredentialsAfterCycle, fallbackResetIso } from "./key-cycle.js";
import { API_ERROR_PATTERN, isAuthError } from "./event-handlers.js";
import { buildCanUseTool } from "./tool-access.js";
import { collectMissingProps } from "./task-property-utils.js";
import { redact } from "./redactor.js";

const logger = createServiceLogger("QueryExecutor");
const IMAGE_ERROR_PATTERN = /Could not process image/i;
const RETRY_DELAYS_MS = [60_000, 120_000, 180_000, 300_000];

export type { QueryHost } from "./query-host.js";
import type { QueryHost } from "./query-host.js";

// ── Query options builder ───────────────────────────────────────────────────

function buildHooks(host: QueryHost): HarnessQueryOptions["hooks"] {
  return {
    PostToolUse: [
      {
        hooks: [
          async (input: HarnessHookInput): Promise<HarnessHookOutput> => {
            if (host.isStopped()) return await Promise.resolve({ continue: false });
            if (input.hook_event_name === "PostToolUse") {
              const raw =
                typeof input.tool_response === "string"
                  ? input.tool_response
                  : JSON.stringify(input.tool_response);
              const { output: redacted, redacted: redactedCount } = redact(raw);
              const output = redacted.slice(0, 500);
              host.connection.sendEvent({
                type: "tool_result",
                tool: input.tool_name,
                output,
                isError: false,
                ...(redactedCount > 0 ? { redactedCount } : {}),
              });
              host.pendingToolOutputs.push({ tool: input.tool_name, output });

              // After PR creation, nudge agent to backfill missing task properties
              if (input.tool_name === "mcp__conveyor__create_pull_request") {
                try {
                  const props = await host.connection.getTaskProperties();
                  const missing = collectMissingProps(props);
                  if (missing.length > 0) {
                    host.connection.postChatMessage(
                      `PR created! Please backfill missing task properties: ${missing.join(", ")}`,
                    );
                  }
                } catch {
                  /* best-effort */
                }
              }
            }
            return await Promise.resolve({ continue: true });
          },
        ],
        timeout: 5,
      },
    ],
  };
}

/**
 * Derive a deterministic UUID v8-style from a session lineage key.
 * The Claude SDK requires UUID format for sessionId; task IDs are cuids.
 * Using sha256(key) ensures the same lineage always maps to the same session.
 */
function taskIdToSessionUuid(lineageKey: string): string {
  const hash = createHash("sha256").update(lineageKey).digest("hex");
  // Format as UUID: xxxxxxxx-xxxx-8xxx-axxx-xxxxxxxxxxxx (v8 + RFC 4122 variant)
  return `${hash.slice(0, 8)}-${hash.slice(8, 12)}-8${hash.slice(13, 16)}-a${hash.slice(17, 20)}-${hash.slice(20, 32)}`;
}

/**
 * Conversation lineage for a task. Review runs (code review, pack-runner
 * orchestration) get their OWN session lineage so:
 *   - a review never resumes the building conversation (it may use a
 *     different model — this was previously "prevented" by the server
 *     clearing `sdkSessionId`, which never actually worked because resume is
 *     filesystem-driven), and
 *   - flipping back to building resumes the ORIGINAL building conversation
 *     untouched.
 */
export function sessionLineageKey(
  taskId: string,
  agentMode: AgentMode,
  runnerMode: AgentRunnerConfig["mode"],
): string {
  return agentMode === "review" || runnerMode === "code-review" ? `${taskId}:review` : taskId;
}

/**
 * Check if a Claude session file exists for the given session UUID + cwd.
 * Session files live at <config-home>/projects/<cwd-slug>/<sessionId>.jsonl
 * (the same CLAUDE_CONFIG_DIR-aware path the PTY transcript tailer uses).
 */
function sessionFileExists(sessionUuid: string, cwd: string): boolean {
  try {
    return existsSync(sessionTranscriptPath(cwd, sessionUuid));
  } catch {
    return false;
  }
}

/** Does a resumable Claude session already exist for this lineage + workspace? */
export function hasExistingSessionFile(
  taskId: string,
  cwd: string,
  lineage: { agentMode: AgentMode; runnerMode: AgentRunnerConfig["mode"] },
): boolean {
  const key = sessionLineageKey(taskId, lineage.agentMode, lineage.runnerMode);
  return sessionFileExists(taskIdToSessionUuid(key), cwd);
}

/**
 * Resolve how a spawned CLI session should start for a `lineageKey` + workspace:
 * resume an existing on-disk transcript (repairing a torn tail first) or create
 * a fresh session under the deterministic UUID. `sessionId` and `resume` are
 * mutually exclusive (SDK/CLI contract) — exactly one is returned.
 *
 * The task-card path (keyed on the task lineage) inlines this same decision to
 * also derive `promptDelivery` from existence; the ad-hoc scratch-pod runner
 * (keyed on its ad-hoc session id) uses this helper directly, so both derive the
 * same deterministic UUID and survive pod restarts via the GCS-FUSE transcript.
 */
export function resolveSessionStart(
  lineageKey: string,
  cwd: string,
): { sessionId?: string; resume?: string } {
  const sessionUuid = taskIdToSessionUuid(lineageKey);
  if (sessionFileExists(sessionUuid, cwd)) {
    repairTornSessionFile(sessionTranscriptPath(cwd, sessionUuid));
    return { resume: sessionUuid };
  }
  return { sessionId: sessionUuid };
}

/**
 * Repair a transcript whose final record was torn by an ungraceful pod death
 * (SIGKILL mid-append, gcsfuse upload cut short): truncate to the end of the
 * last complete, parseable line so `--resume` gets a well-formed JSONL.
 * Returns true when a truncation was performed.
 */
export function repairTornSessionFile(path: string): boolean {
  try {
    if (!existsSync(path)) return false;
    const content = readFileSync(path, "utf8");
    if (content.length === 0) return false;

    // Phase 1: drop a trailing partial line (no terminating newline).
    let keepEnd = content.length;
    if (!content.endsWith("\n")) {
      // 0 when no newline exists at all.
      keepEnd = content.lastIndexOf("\n") + 1;
    }
    // Phase 2: drop trailing terminated lines that don't parse as JSON
    // (a write torn mid-line that still got its newline flushed).
    while (keepEnd > 0) {
      const prevNewline = content.lastIndexOf("\n", keepEnd - 2);
      const line = content.slice(prevNewline + 1, keepEnd - 1).trim();
      if (line.length > 0) {
        try {
          JSON.parse(line);
          // Trailing line is whole — done.
          break;
        } catch {
          /* unparseable — trim it */
        }
      }
      keepEnd = prevNewline + 1;
    }

    if (keepEnd === content.length) return false;
    truncateSync(path, Buffer.byteLength(content.slice(0, keepEnd), "utf8"));
    logger.warn("Repaired torn transcript before resume", {
      path,
      trimmedBytes: content.length - keepEnd,
    });
    return true;
  } catch {
    // Best-effort — a failed repair falls through to the CLI.
    return false;
  }
}

export interface PromptDeliveryInputs {
  harnessKind: HarnessKind;
  runnerMode: AgentRunnerConfig["mode"];
  isAuto: boolean;
  agentMode: AgentMode;
  isFollowUp: boolean;
  hasExistingSession: boolean;
}

/**
 * Decide how a query's prompt reaches the agent.
 *
 * "prefill" (paste into the TUI input, let the human review/edit/Enter) applies
 * only to the INITIAL instructions of a `help` task chat under the PTY harness.
 * Everything else submits immediately:
 *   - SDK harness: no interactive input box exists.
 *   - code-review runner: autonomous, no human at the TUI.
 *   - auto tasks (isAuto or agentMode "auto"): full-auto is the whole point.
 *   - follow-ups: the human already directed the agent via chat.
 *   - resumes: the conversation exists; re-prefilling the initial
 *     instructions into it would be confusing.
 *   - discovery (interactive): a fresh interactive card auto-submits its
 *     planner preamble so the agent starts helping plan immediately, rather
 *     than parking an empty TUI and waiting on the human to press Enter.
 *
 * The pm runner mode is NOT excluded: fresh Planning-status cards boot their
 * task pod as a pm runner (computeRunnerMode server-side), so pm + `help` IS
 * the human-facing read-only assist card that should park a prefilled TUI.
 * Autonomous pm flows are already caught by the auto/isAuto check above.
 */
export function resolvePromptDelivery(inputs: PromptDeliveryInputs): "submit" | "prefill" {
  if (inputs.harnessKind !== "pty") return "submit";
  if (inputs.runnerMode === "code-review") return "submit";
  if (inputs.isFollowUp || inputs.hasExistingSession) return "submit";
  if (inputs.isAuto || inputs.agentMode === "auto") return "submit";
  // Only read-only `help` parks a prefill. discovery/building/review initial
  // queries auto-run — discovery submits its planner preamble to start helping
  // plan immediately, and a fresh-disk build boot with nobody at the terminal
  // must never stall waiting on an unsubmitted prompt.
  if (inputs.agentMode !== "help") return "submit";
  return "prefill";
}

function isReadOnlyMode(mode: AgentMode, hasExitedPlanMode: boolean): boolean {
  return mode === "discovery" || mode === "help" || (mode === "auto" && !hasExitedPlanMode);
}

function buildDisallowedTools(
  settings: { disallowedTools?: string[] },
  mode: AgentMode,
  hasExitedPlanMode: boolean,
): string[] | undefined {
  const modeDisallowed = isReadOnlyMode(mode, hasExitedPlanMode)
    ? ["TodoWrite", "TodoRead", "NotebookEdit"]
    : [];
  const configured = settings.disallowedTools ?? [];
  const combined = [...configured, ...modeDisallowed];
  return combined.length > 0 ? combined : undefined;
}

function buildQueryOptions(host: QueryHost, context: TaskContext): HarnessQueryOptions {
  const settings = context.agentSettings ?? host.config.agentSettings ?? {};
  const mode = host.agentMode;

  // Read-only modes (discovery, help) use the SDK's native "plan" permission
  // mode which enforces no-execution (blocks file edits and destructive
  // commands). canUseTool is still registered to intercept ExitPlanMode for
  // custom validation (plan/SP/title checks, triggerIdentification, mode
  // restart). The auto pre-exit arm is a residual safety net: auto now boots
  // with hasExitedPlanMode=true (ModeController.canBypassPlanning), so in
  // normal flow auto is never read-only.
  const isReadOnly = isReadOnlyMode(mode, host.hasExitedPlanMode);
  const needsCanUseTool = isReadOnly;

  const systemPromptText = buildSystemPrompt(
    host.config.mode,
    context,
    { ...host.config, isAuto: host.isAuto },
    host.setupLog,
    mode,
  );
  const settingSources = (settings.settingSources ?? ["user", "project"]) as (
    | "user"
    | "project"
    | "local"
  )[];

  return {
    model: context.model || host.config.model,
    systemPrompt: {
      type: "preset",
      preset: "claude_code",
      append: systemPromptText || undefined,
    },
    settingSources,
    cwd: host.config.workspaceDir,
    permissionMode: needsCanUseTool ? "plan" : "bypassPermissions",
    allowDangerouslySkipPermissions: !needsCanUseTool,
    canUseTool: buildCanUseTool(host),
    // The spawned CLI never sees `systemPrompt` (an SDK-only option) — deliver
    // the same text via `--append-system-prompt`. PTY-only: the SDK harness
    // already receives it through systemPrompt.append.
    ...(host.harnessKind === "pty" && systemPromptText
      ? { appendSystemPrompt: systemPromptText }
      : {}),
    // Auto mode pre-exit (residual sessions only — auto now boots post-exit):
    // after the ExitPlanMode hook allows the call, the CLI's plan dialog still
    // renders — press Enter so the autonomous agent continues building in the
    // same session (Conveyor validated in the hook).
    planDialogAutoAccept: mode === "auto" && !host.hasExitedPlanMode,
    tools: { type: "preset" as const, preset: "claude_code" as const },
    mcpServers: {
      conveyor: createConveyorMcpServer(host.harness, host.connection, host.config, context, mode),
      // Baked-in browser automation (claudespace pods) — omitted when the
      // binary isn't on PATH (dev machines, legacy images).
      ...(() => {
        const playwright = resolvePlaywrightMcpServer();
        return playwright ? { playwright } : {};
      })(),
    },
    sandbox: context.useSandbox ? { enabled: true } : { enabled: false },
    hooks: buildHooks(host),
    maxTurns: settings.maxTurns,
    effort: settings.effort,
    thinking: settings.thinking,
    betas: settings.betas,
    abortController: host.abortController ?? undefined,
    disallowedTools: buildDisallowedTools(settings, mode, host.hasExitedPlanMode),
    enableFileCheckpointing: settings.enableFileCheckpointing,
    stderr: (data: string) => {
      logger.warn("Claude Code stderr", { data: data.trimEnd() });
    },
  };
}

// ── Multimodal prompt builder ────────────────────────────────────────────

type ImageMediaType = "image/gif" | "image/jpeg" | "image/png" | "image/webp";

function buildMultimodalPrompt(
  textPrompt: string,
  context: TaskContext,
  skipImages = false,
): string | MultimodalBlock[] {
  if (skipImages) return textPrompt;

  const taskImages = (context.files ?? []).filter(
    (f) => f.content && f.contentEncoding === "base64",
  );
  const chatImages: { fileName: string; mimeType: string; content: string }[] = [];
  for (const msg of context.chatHistory) {
    for (const f of msg.files ?? []) {
      if (f.content && f.contentEncoding === "base64") {
        chatImages.push({ fileName: f.fileName, mimeType: f.mimeType, content: f.content });
      }
    }
  }

  if (taskImages.length === 0 && chatImages.length === 0) return textPrompt;

  const blocks: MultimodalBlock[] = [{ type: "text", text: textPrompt }];
  for (const file of taskImages) {
    blocks.push({
      type: "image",
      source: {
        type: "base64",
        media_type: file.mimeType as ImageMediaType,
        data: file.content ?? "",
      },
    });
    blocks.push({ type: "text", text: `[Attached image: ${file.fileName} (${file.mimeType})]` });
  }
  for (const file of chatImages) {
    blocks.push({
      type: "image",
      source: { type: "base64", media_type: file.mimeType as ImageMediaType, data: file.content },
    });
    blocks.push({ type: "text", text: `[Chat image: ${file.fileName} (${file.mimeType})]` });
  }
  return blocks;
}

// ── Follow-up prompt builder ─────────────────────────────────────────────

async function buildFollowUpPrompt(
  host: QueryHost,
  context: TaskContext,
  followUpContent: string | MultimodalBlock[],
): Promise<string | MultimodalBlock[]> {
  const isPmMode = host.config.mode === "pm";
  const followUpText =
    typeof followUpContent === "string"
      ? followUpContent
      : followUpContent
          .filter((b): b is Extract<MultimodalBlock, { type: "text" }> => b.type === "text")
          .map((b) => b.text)
          .join("\n");

  const followUpImages =
    typeof followUpContent === "string"
      ? []
      : followUpContent.filter(
          (b): b is Extract<MultimodalBlock, { type: "image" }> => b.type === "image",
        );

  const textPrompt = isPmMode
    ? `${await buildInitialPrompt(host.config.mode, context, host.isAuto, host.agentMode)}\n\n---\n\nThe team says:\n${followUpText}`
    : followUpText;

  const isPty = host.harnessKind === "pty";
  if (isPmMode) {
    // Under PTY skipImages yields a string, so follow-up image blocks are not
    // appended either — they stay reachable via chat attachments (MCP).
    const prompt = buildMultimodalPrompt(textPrompt, context, isPty);
    if (followUpImages.length > 0 && Array.isArray(prompt)) {
      prompt.push(...followUpImages);
    }
    return prompt;
  }
  if (followUpImages.length > 0) {
    if (isPty) {
      // Image blocks carry no fileId, so point at the attachment tools.
      const refs = followUpImages.map(
        () => `[Image attachment — use list_task_files / get_attachment to view]`,
      );
      return [textPrompt, ...refs].join("\n");
    }
    return [{ type: "text", text: textPrompt }, ...followUpImages];
  }
  return textPrompt;
}

// ── SDK query execution ──────────────────────────────────────────────────

/** Pass events through, firing `onFirst` before the first non-system event. */
async function* notifyOnFirstEvent(
  inner: AsyncGenerator<HarnessEvent, void>,
  onFirst: () => Promise<void>,
): AsyncGenerator<HarnessEvent, void> {
  let fired = false;
  for await (const event of inner) {
    if (!fired && event.type !== "system") {
      fired = true;
      await onFirst();
    }
    yield event;
  }
}

// How long a submitted PTY query may stay silent (zero transcript events)
// before the agent reports waiting_for_input instead of running. A healthy
// turn writes its first transcript record well inside this window; a TUI
// parked on an unexpected dialog (trust prompt, onboarding wizard, login
// screen) never produces one, which previously left the card stuck on
// "working" indefinitely.
const PARKED_TUI_GRACE_MS = 45_000;

/**
 * Status-accuracy watchdog for submitted PTY queries. Display-only: the
 * query keeps draining either way, `_state` in SessionRunner is untouched
 * (so chat-message supersede and idle-timer behavior are unchanged), and the
 * first event flips the reported status back to running.
 */
export async function* watchForParkedTui(
  inner: AsyncGenerator<HarnessEvent, void>,
  host: Pick<QueryHost, "connection" | "callbacks">,
): AsyncGenerator<HarnessEvent, void> {
  let parked = false;
  // The generator body runs lazily, so the timer starts when the consumer
  // begins draining — i.e. when the spawned CLI is live and the prompt has
  // been submitted.
  const timer = setTimeout(() => {
    parked = true;
    host.connection.emitStatus("waiting_for_input");
    void host.callbacks.onStatusChange("waiting_for_input");
  }, PARKED_TUI_GRACE_MS);
  try {
    for await (const event of inner) {
      clearTimeout(timer);
      if (parked) {
        parked = false;
        host.connection.emitStatus("running");
        await host.callbacks.onStatusChange("running");
      }
      yield event;
    }
  } finally {
    clearTimeout(timer);
  }
}

export async function runSdkQuery(
  host: QueryHost,
  context: TaskContext,
  followUpContent?: string | MultimodalBlock[],
  promptDeliveryOverride?: "submit" | "prefill",
): Promise<void> {
  if (host.isStopped()) return;

  const mode = host.agentMode;
  const isDiscoveryLike = mode === "discovery" || mode === "help";

  // Use a deterministic UUID derived from the session lineage (taskId, with a
  // ":review" variant for review runs) as the session ID. Sessions persist via
  // GCS-FUSE, so resuming by the derived UUID works across pod restarts
  // without tracking SDK-generated session IDs. See resolveSessionStart for
  // the sessionId/resume mutual-exclusivity contract.
  const sessionStart = resolveSessionStart(
    sessionLineageKey(context.taskId, mode, host.config.mode),
    host.config.workspaceDir,
  );
  const hasExistingSession = !!sessionStart.resume;
  const promptDelivery =
    promptDeliveryOverride ??
    resolvePromptDelivery({
      harnessKind: host.harnessKind,
      runnerMode: host.config.mode,
      isAuto: host.isAuto,
      agentMode: mode,
      isFollowUp: !!followUpContent,
      hasExistingSession,
    });
  const options = {
    ...buildQueryOptions(host, context),
    promptDelivery,
    ...(sessionStart.sessionId ? { sessionId: sessionStart.sessionId } : {}),
  };
  const resume = sessionStart.resume;

  if (followUpContent) {
    await runFollowUpQuery(host, context, options, resume, followUpContent);
    return;
  }
  if (isDiscoveryLike && promptDelivery !== "prefill") {
    // SDK harness (or a resumed session): there is no TUI input box to
    // pre-fill, and discovery must not auto-run — wait for a chat message.
    return;
  }
  await runInitialQuery(host, context, options, resume, promptDelivery);
}

/** Dispatch a follow-up to the prefill or submit path per the resolved delivery. */
async function runFollowUpQuery(
  host: QueryHost,
  context: TaskContext,
  options: HarnessQueryOptions,
  resume: string | undefined,
  followUpContent: string | MultimodalBlock[],
): Promise<void> {
  if (options.promptDelivery === "prefill") {
    await runPrefilledFollowUp(host, context, options, resume, followUpContent);
    return;
  }
  const prompt = await buildFollowUpPrompt(host, context, followUpContent);
  const agentQuery = host.harness.executeQuery({
    prompt: typeof prompt === "string" ? prompt : host.createInputStream(prompt),
    options: { ...options },
    resume,
  });
  await trackAndRun(host, context, options, agentQuery);
}

/**
 * A follow-up delivered as a prefill: paste ONLY the message text into the TUI
 * input (the human reviews/edits/Enters it — e.g. a Refine prompt). Context
 * parity with the submitted pm follow-up (which prepends the full initial
 * prompt) is preserved by moving that text onto `--append-system-prompt`.
 * Images in the follow-up stay reachable via chat attachments.
 */
async function runPrefilledFollowUp(
  host: QueryHost,
  context: TaskContext,
  options: HarnessQueryOptions,
  resume: string | undefined,
  followUpContent: string | MultimodalBlock[],
): Promise<void> {
  const followUpText =
    typeof followUpContent === "string"
      ? followUpContent
      : followUpContent
          .filter((b): b is Extract<MultimodalBlock, { type: "text" }> => b.type === "text")
          .map((b) => b.text)
          .join("\n");

  const queryOptions = { ...options };
  if (host.config.mode === "pm" && !resume) {
    // Fresh pm session: the submitted path would prepend the full initial
    // prompt — deliver that context via the system prompt instead.
    const initialPrompt = await buildInitialPrompt(
      host.config.mode,
      context,
      host.isAuto,
      host.agentMode,
    );
    queryOptions.appendSystemPrompt = [queryOptions.appendSystemPrompt, initialPrompt]
      .filter(Boolean)
      .join("\n\n")
      .slice(0, APPEND_SYSTEM_PROMPT_MAX_CHARS);
  }

  let agentQuery = host.harness.executeQuery({
    prompt: followUpText,
    options: queryOptions,
    resume,
  });
  // Parked until the human submits — first transcript event flips to running.
  agentQuery = notifyOnFirstEvent(agentQuery, async () => {
    host.connection.emitStatus("running");
    await host.callbacks.onStatusChange("running");
  });
  await trackAndRun(host, context, queryOptions, agentQuery);
}

/**
 * Drive a PASSIVE turn: the parked CLI is already emitting transcript records
 * because a human typed into the idle Connected-TUI (no prompt from us). Process
 * those events through the same pipeline as a normal turn — status, chat mirror,
 * plan sync, completion tracking — so the intervention is reflected everywhere.
 * No retry and no parked-TUI watchdog: a passive turn is human-paced and there
 * is nothing to resubmit. No-op on a harness without a parked session.
 */
export async function runPassiveTurn(host: QueryHost, context: TaskContext): Promise<void> {
  if (host.isStopped()) return;
  if (!host.harness.executePassiveTurn) return;
  const options = buildQueryOptions(host, context);
  const passiveQuery = host.harness.executePassiveTurn(options);
  host.activeQuery = passiveQuery;
  try {
    await processEvents(passiveQuery, context, host);
  } finally {
    host.activeQuery = null;
  }
}

/** Track the active query while runWithRetry drains it. */
async function trackAndRun(
  host: QueryHost,
  context: TaskContext,
  options: HarnessQueryOptions,
  agentQuery: AsyncGenerator<HarnessEvent, void>,
): Promise<void> {
  // Prefill queries are parked by design (already reported as
  // waiting_for_input); SDK queries emit an init event immediately. Only a
  // submitted PTY turn can silently park on an unexpected CLI dialog.
  if (host.harnessKind === "pty" && options.promptDelivery !== "prefill") {
    agentQuery = watchForParkedTui(agentQuery, host);
  }
  host.activeQuery = agentQuery;
  try {
    await runWithRetry(agentQuery, context, host, options);
  } finally {
    host.activeQuery = null;
  }
}

/** Defensive bound for `--append-system-prompt` argv size (Linux MAX_ARG_STRLEN is 128KiB). */
const APPEND_SYSTEM_PROMPT_MAX_CHARS = 96_000;

/** The most recent non-empty user chat message — the text a human actually typed. */
export function latestUserMessageText(context: TaskContext): string | null {
  for (let i = context.chatHistory.length - 1; i >= 0; i--) {
    const msg = context.chatHistory[i];
    if (msg.role === "user" && msg.content.trim()) return msg.content.trim();
  }
  return null;
}

/**
 * Pick what lands in the TUI input for the INITIAL query, and what rides
 * `--append-system-prompt`, based on the resolved delivery.
 *
 * - **submit**: paste the full `initialPrompt` body — it auto-Enters, so nothing
 *   is left parked in the input.
 * - **prefill**: paste ONLY the human's latest chat message, or an EMPTY box
 *   when there is none — NEVER the full task body, which would park unsubmitted
 *   and block the human from typing a follow-up (the original auto-mode
 *   "re-prefill" bug). The full instructions/context move onto the system
 *   prompt so the agent still has them once the human submits. The pasted
 *   prefill is text-only; images stay reachable via chat/task attachments (MCP).
 */
export function selectInitialPromptInput(
  promptDelivery: "submit" | "prefill",
  initialPrompt: string,
  context: TaskContext,
  baseAppendSystemPrompt: string | undefined,
  harnessKind: HarnessKind,
): { prompt: string | MultimodalBlock[]; appendSystemPrompt: string | undefined } {
  if (promptDelivery === "prefill") {
    return {
      prompt: latestUserMessageText(context) ?? "",
      appendSystemPrompt: [baseAppendSystemPrompt, initialPrompt]
        .filter(Boolean)
        .join("\n\n")
        .slice(0, APPEND_SYSTEM_PROMPT_MAX_CHARS),
    };
  }
  return {
    // On the PTY harness image blocks would be pasted into the TUI as text —
    // the prompt body already links each image (get_attachment / downloadUrl).
    prompt: buildMultimodalPrompt(initialPrompt, context, harnessKind === "pty"),
    appendSystemPrompt: baseAppendSystemPrompt,
  };
}

/** Build and run the initial (non-follow-up) query for the current mode. */
async function runInitialQuery(
  host: QueryHost,
  context: TaskContext,
  options: HarnessQueryOptions,
  resume: string | undefined,
  promptDelivery: "submit" | "prefill",
): Promise<void> {
  const initialPrompt = await buildInitialPrompt(
    host.config.mode,
    context,
    host.isAuto,
    host.agentMode,
  );

  const { prompt, appendSystemPrompt } = selectInitialPromptInput(
    promptDelivery,
    initialPrompt,
    context,
    options.appendSystemPrompt,
    host.harnessKind,
  );
  const queryOptions = { ...options, appendSystemPrompt };

  let agentQuery = host.harness.executeQuery({
    prompt: host.createInputStream(prompt),
    options: queryOptions,
    resume,
  });
  if (promptDelivery === "prefill") {
    // The prompt sits unsubmitted in the TUI; the transcript stays empty
    // until the human presses Enter, so the first non-system event is the
    // "human engaged" signal — report running so the runner can leave
    // waiting_for_input and stop the prefill-wait idle timer.
    agentQuery = notifyOnFirstEvent(agentQuery, async () => {
      host.connection.emitStatus("running");
      await host.callbacks.onStatusChange("running");
    });
  }
  await trackAndRun(host, context, queryOptions, agentQuery);
}

// ── Retry logic ──────────────────────────────────────────────────────────

async function buildRetryQuery(
  host: QueryHost,
  context: TaskContext,
  options: HarnessQueryOptions,
  lastErrorWasImage: boolean,
): Promise<AsyncGenerator<HarnessEvent, void>> {
  if (lastErrorWasImage) {
    host.connection.postChatMessage(
      "An attached image could not be processed. Retrying without images...",
    );
  }
  const retryPrompt = buildMultimodalPrompt(
    await buildInitialPrompt(host.config.mode, context, host.isAuto, host.agentMode),
    context,
    lastErrorWasImage || host.harnessKind === "pty",
  );
  return host.harness.executeQuery({
    prompt: host.createInputStream(retryPrompt),
    // Strip sessionId on retry — if the failing query partially created a
    // session with that ID, passing it again would error. Let the SDK
    // auto-generate on retry attempts.
    options: { ...options, sessionId: undefined },
    resume: undefined,
  });
}

export async function handleAuthError(
  context: TaskContext,
  host: QueryHost,
  options: HarnessQueryOptions,
): Promise<void> {
  host.connection.postChatMessage("Authentication expired. Re-bootstrapping credentials...");

  const refreshed = await host.connection.refreshAuthToken();
  if (!refreshed) {
    // No Claude credential could be resolved (none configured, or the bootstrap
    // has no Claude token to hand back). Do NOT process.exit — that crash-loops
    // the pod, and an agent that can't run still leaves an empty workspace that
    // wedges teardown (2026-07-05 canary). Post an actionable message and return
    // so the runner falls back to its idle/dormant wait: stays connected, burns
    // no tokens, and resumes if a human configures a credential and re-sends.
    host.connection.postChatMessage(
      "⚠️ No Claude credential is configured for this project — the agent can't run and " +
        "is now idle. Add a Claude Code OAuth token (or Anthropic API key) in Project " +
        "Settings, then resume this task.",
    );
    host.connection.sendEvent({
      type: "error",
      message: "No Claude credential available — agent idle (dormant)",
    });
    return;
  }

  // Clear session since it's tied to the old token
  context.claudeSessionId = null;
  host.connection.storeSessionId("");

  const freshPrompt = buildMultimodalPrompt(
    await buildInitialPrompt(host.config.mode, context, host.isAuto, host.agentMode),
    context,
    host.harnessKind === "pty",
  );
  const freshQuery = host.harness.executeQuery({
    prompt: host.createInputStream(freshPrompt),
    options: { ...options, sessionId: undefined },
    resume: undefined,
  });
  return runWithRetry(freshQuery, context, host, options);
}

async function handleStaleSession(
  context: TaskContext,
  host: QueryHost,
  options: HarnessQueryOptions,
): Promise<void> {
  context.claudeSessionId = null;
  host.connection.storeSessionId("");
  const freshPrompt = buildMultimodalPrompt(
    await buildInitialPrompt(host.config.mode, context, host.isAuto, host.agentMode),
    context,
    host.harnessKind === "pty",
  );
  const freshQuery = host.harness.executeQuery({
    prompt: host.createInputStream(freshPrompt),
    options: { ...options, sessionId: undefined },
    resume: undefined,
  });
  return runWithRetry(freshQuery, context, host, options);
}

async function waitForRetryDelay(host: QueryHost, delayMs: number): Promise<void> {
  await new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, delayMs);
    const checkStopped = setInterval(() => {
      if (host.isStopped()) {
        clearTimeout(timer);
        clearInterval(checkStopped);
        resolve();
      }
    }, 1000);
    setTimeout(() => clearInterval(checkStopped), delayMs + 100);
  });
}

function isStaleOrExitedSession(error: unknown, context: TaskContext): boolean {
  if (!(error instanceof Error)) return false;
  if (error.message.includes("No conversation found with session ID")) return true;
  return !!context.claudeSessionId && error.message.includes("process exited");
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return String(error);
}

function isRetriableError(error: unknown): boolean {
  const message = getErrorMessage(error);
  return API_ERROR_PATTERN.test(message) || IMAGE_ERROR_PATTERN.test(message);
}

function classifyImageError(error: unknown): boolean {
  return IMAGE_ERROR_PATTERN.test(getErrorMessage(error));
}

async function emitRetryStatus(host: QueryHost, attempt: number, delayMs: number): Promise<void> {
  const delayMin = Math.round(delayMs / 60_000);
  host.connection.postChatMessage(
    `API error encountered. Retrying in ${delayMin} minute${delayMin > 1 ? "s" : ""}... (attempt ${attempt + 1}/${RETRY_DELAYS_MS.length})`,
  );
  host.connection.sendEvent({
    type: "error",
    message: `API error, retrying in ${delayMin}m (${attempt + 1}/${RETRY_DELAYS_MS.length})`,
  });
  host.connection.emitStatus("waiting_for_input");
  await host.callbacks.onStatusChange("waiting_for_input");

  await waitForRetryDelay(host, delayMs);

  host.connection.emitStatus("running");
  await host.callbacks.onStatusChange("running");
}

function handleRateLimitPause(host: QueryHost, rateLimitResetsAt: string): void {
  host.wasRateLimited = true;
  host.connection.emitRateLimitPause(rateLimitResetsAt);
  host.connection.postChatMessage(
    `Rate limited. The task will be automatically re-queued and resume after ${new Date(rateLimitResetsAt).toLocaleString()}.`,
  );
}

/** Ceiling on swaps per session — bounds pathological loops (e.g. every key on
 *  the same exhausted account) without a server round-trip per check. */
const MAX_KEY_CYCLES = 5;

/**
 * A hard usage cap ended the turn. Try to cycle to another eligible key
 * (server stamps the exhausted one and returns the replacement credential);
 * fall back to the classic rate-limit pause when cycling isn't possible.
 * Mirrors handleAuthError's recover-and-re-run shape.
 */
async function handleUsageCapRejection(
  context: TaskContext,
  host: QueryHost,
  options: HarnessQueryOptions,
  rateLimitType: string,
  resetsAt: string | undefined,
): Promise<void> {
  const pauseAt = resetsAt ?? fallbackResetIso(rateLimitType);
  if (host.keyCycleCount >= MAX_KEY_CYCLES) {
    handleRateLimitPause(host, pauseAt);
    return;
  }
  host.keyCycleCount += 1;

  let response: Awaited<ReturnType<AgentConnection["cycleCodingAgentKey"]>>;
  try {
    response = await host.connection.cycleCodingAgentKey(rateLimitType, resetsAt);
  } catch (error) {
    host.connection.postChatMessage(
      `Usage cap hit and key cycling failed (${getErrorMessage(error)}) — pausing until ${new Date(pauseAt).toLocaleString()}.`,
    );
    handleRateLimitPause(host, pauseAt);
    return;
  }

  if (!response.cycled) {
    // The server already stamped the key and requeued the task.
    handleRateLimitPause(host, response.resetsAt);
    return;
  }

  applyCycledKeyEnv(response.envVars);
  await syncCredentialsAfterCycle();
  // The live/parked TUI process is still authenticated as the exhausted key,
  // and PTY reuse fingerprints don't cover the token — kill it so the next
  // spawn re-runs prepareEnvironment against the fresh credentials file.
  await host.harness.dispose?.();

  host.connection.postChatMessage(
    `Usage cap hit — switched to key **${response.label}** and resuming.`,
  );

  // Fresh session under the new key (same recovery shape as handleAuthError):
  // the rebuilt initial prompt carries the chat history, so the interrupted
  // turn re-runs without the old transcript.
  context.claudeSessionId = null;
  host.connection.storeSessionId("");
  const freshPrompt = buildMultimodalPrompt(
    await buildInitialPrompt(host.config.mode, context, host.isAuto, host.agentMode),
    context,
    host.harnessKind === "pty",
  );
  const freshQuery = host.harness.executeQuery({
    prompt: host.createInputStream(freshPrompt),
    options: { ...options, sessionId: undefined },
    resume: undefined,
  });
  return runWithRetry(freshQuery, context, host, options);
}

type RetryOutcome = { action: "return" } | { action: "continue"; lastErrorWasImage: boolean };

function handleRetryError(
  error: unknown,
  context: TaskContext,
  host: QueryHost,
  options: HarnessQueryOptions,
  prevImageError: boolean,
): RetryOutcome | Promise<void> {
  if (isStaleOrExitedSession(error, context) && context.claudeSessionId) {
    return handleStaleSession(context, host, options);
  }
  if (isAuthError(getErrorMessage(error))) {
    return handleAuthError(context, host, options);
  }
  if (!isRetriableError(error)) throw error;
  return { action: "continue", lastErrorWasImage: classifyImageError(error) || prevImageError };
}

type ProcessResult =
  | { action: "return" }
  | { action: "return_promise"; promise: Promise<void> }
  | { action: "continue"; lastErrorWasImage: boolean };

function handleProcessResult(
  result: Awaited<ReturnType<typeof processEvents>>,
  context: TaskContext,
  host: QueryHost,
  options: HarnessQueryOptions,
): ProcessResult {
  if (result.modeRestart || host.isStopped()) return { action: "return" };

  if (result.rateLimitRejectedType || result.rateLimitResetsAt) {
    return {
      action: "return_promise",
      promise: handleUsageCapRejection(
        context,
        host,
        options,
        result.rateLimitRejectedType ?? "unknown",
        result.rateLimitResetsAt,
      ),
    };
  }

  // Handle stale session result errors (same recovery as thrown exceptions)
  if (result.staleSession && context.claudeSessionId) {
    return { action: "return_promise", promise: handleStaleSession(context, host, options) };
  }

  // Handle auth errors — re-bootstrap token and retry
  if (result.authError) {
    return { action: "return_promise", promise: handleAuthError(context, host, options) };
  }

  if (!result.retriable) return { action: "return" };
  return {
    action: "continue",
    lastErrorWasImage: IMAGE_ERROR_PATTERN.test(result.resultSummary ?? ""),
  };
}

async function runWithRetry(
  initialQuery: AsyncGenerator<HarnessEvent, void>,
  context: TaskContext,
  host: QueryHost,
  options: HarnessQueryOptions,
): Promise<void> {
  let lastErrorWasImage = false;

  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    if (host.isStopped()) return;

    const agentQuery =
      attempt === 0
        ? initialQuery
        : await buildRetryQuery(host, context, options, lastErrorWasImage);

    try {
      const result = await processEvents(agentQuery, context, host);
      const outcome = handleProcessResult(result, context, host, options);
      if (outcome.action === "return") return;
      if (outcome.action === "return_promise") return outcome.promise;
      lastErrorWasImage = outcome.lastErrorWasImage;
    } catch (error) {
      const outcome = handleRetryError(error, context, host, options, lastErrorWasImage);
      if (outcome instanceof Promise) return outcome;
      if (outcome.action === "return") return;
      lastErrorWasImage = outcome.lastErrorWasImage;
    }

    if (attempt >= RETRY_DELAYS_MS.length) {
      host.connection.postChatMessage(
        `Agent shutting down after ${RETRY_DELAYS_MS.length} failed retry attempts due to API errors. ` +
          `The task will resume automatically when the codespace restarts.`,
      );
      return;
    }

    await emitRetryStatus(host, attempt, RETRY_DELAYS_MS[attempt]);
  }
}
